import {Component, OnDestroy, OnInit} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {Subscription} from 'rxjs';
import {BusinessDataService} from '../../../../../data-repositories/business-data.service';
import {CountryService} from '../../../../../data-repositories/country-service';
import {FeaturesService} from '../../../../../data-repositories/features.service';
import {LanguageService} from '../../../../../data-repositories/language.service';
import {Country, StateInfo} from '../../../../../models/country';
import {AffiliationDetails, Regulatory} from '../../../../../models/regulatory';
import {State} from '../../../../../models/state';
import {User} from '../../../../../models/user';
import {ValidationInfoService} from '../../../../../repositories/validation-info.service';
import {formLog} from '../../../../../services/debug-logger';

@Component({
  standalone: false,
  selector: 'app-regulatory',
  templateUrl: './regulatory.component.html',
  styleUrls: ['./regulatory.component.scss'],
})
export class RegulatoryComponent implements OnInit, OnDestroy {
  private emailPattern =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  private phonePattern = /^\d{5,15}$/;

  public regulatoryModel: Regulatory = new Regulatory();
  public userModel: User = new User();

  public countryList: Array<Country>;
  public addressStates: Array<State>;

  public regulatoryControlForm: FormGroup;
  public regulatoryAffiliationForm: FormGroup;
  public employeePubTradeForm: FormGroup;
  public controlPubTradeForm: FormGroup;

  get regulatoryAffiliationFormControls() {
    return this.regulatoryAffiliationForm.controls;
  }

  public isRequiredField(form: FormGroup, fieldName: string): boolean {
    const control = form.controls[fieldName];
    return control.invalid
      && ((control.dirty || control.touched) && this.submitted)
      && !!control.errors?.required;
  }

  public hasErrorField(form: FormGroup, fieldName: string, error: string): boolean {
    const control = form.controls[fieldName];
    return control.invalid
      && (control.dirty || control.touched || this.submitted)
      && control.hasError(error);
  }

  public submitted: boolean;
  private subscription: Subscription;
  private formSub = new Subscription();

  public regInfoIsOpen: boolean;
  public regInfo: string;

  constructor(
    private service: BusinessDataService,
    private countryService: CountryService,
    private formBuilder: FormBuilder,
    private validationService: ValidationInfoService,
    public langService: LanguageService,
    public featureService: FeaturesService
  ) {
    this.createForm();
    this.formSub.add(service.GetUser().subscribe((s) => {
      this.regulatoryModel = s.model.regulatoryModel;
      this.userModel = s.model;
      this.patchFormFromModel();
      if (this.regulatoryModel.affiliationDetails.mailingCountry) {
        this.onCountryChanged(false);
      }
    }));

    this.formSub.add(this.countryService.getCountryList().subscribe((cl) => {
      this.countryList = cl;
    }));

    this.formSub.add(this.langService.getInfoFieldSubject().subscribe((ifm) => {
      this.regInfo = ifm.find((field) => field.key === 'Reg Info').info;
    }));
  }

  ngOnInit() {
    this.patchFormFromModel();

    this.formSub.add(this.regulatoryControlForm.valueChanges.subscribe(() => this.syncModelFromForm()));
    this.formSub.add(this.employeePubTradeForm.valueChanges.subscribe(() => this.syncModelFromForm()));
    this.formSub.add(this.controlPubTradeForm.valueChanges.subscribe(() => this.syncModelFromForm()));
    this.formSub.add(this.regulatoryAffiliationForm.valueChanges.subscribe(() => this.syncModelFromForm()));

    this.subscription = this.validationService.onUserUpdate.subscribe(() => {
      this.submitted = true;
      this.markAllFieldsAsTouched();
      this.validationService.validate(
        this.validateRegulatoryDetails() &&
        ((this.regulatoryAffiliationForm.valid &&
            this.regulatoryModel.regulatoryAffiliation.status) ||
          !this.regulatoryModel.regulatoryAffiliation.status), 'RegulatoryComponent'
      );
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.formSub.unsubscribe();
  }

  onAddressCountrySelected(countryName: string) {
    this.countryService.getStatesByCountryCode(countryName, (sl) => {
      this.addressStates = sl;
    });
  }

  regulatoryAffiliationChanged(value: any) {
    if (!value) {
      this.regulatoryModel.affiliationDetails = new AffiliationDetails();
    }
  }

  private createForm() {
    this.regulatoryControlForm = this.formBuilder.group({
      isOn: new FormControl(this.regulatoryModel.regulatoryControl.status),
      details: new FormControl('', Validators.required),
    });

    this.employeePubTradeForm = this.formBuilder.group({
      isOn: new FormControl(),
      details: new FormControl('', Validators.required),
    });

    this.controlPubTradeForm = this.formBuilder.group({
      isOn: new FormControl(),
      details: new FormControl('', Validators.required),
    });

    this.regulatoryAffiliationForm = this.formBuilder.group({
      isOn: new FormControl(this.regulatoryModel.regulatoryAffiliation.status),
      details: new FormControl('', Validators.required),
      relationship: new FormControl('', Validators.required),
      personName: new FormControl('', Validators.required),
      company: new FormControl('', Validators.required),
      country: new FormControl('', Validators.required),
      state: new FormControl('', Validators.required),
      city: new FormControl('', Validators.required),
      postalCode: new FormControl('', Validators.required),
      address: new FormControl('', Validators.required),
      phone: new FormControl('', [Validators.required, this.phoneValidation]),
      email: new FormControl('', [Validators.required, this.emailValidation]),
    });
  }

  private emailValidation: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    return !control.value || this.emailPattern.test(control.value)
      ? null
      : {email: {text: 'Invalid email'}};
  };

  private phoneValidation: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    return !control.value || this.phonePattern.test(control.value)
      ? null
      : {phone: {text: 'Invalid phone'}};
  };

  private patchFormFromModel(): void {
    if (!this.regulatoryModel || !this.regulatoryControlForm) {
      return;
    }

    const regulatoryControl = this.regulatoryModel.regulatoryControl;
    const regulatoryAffiliation = this.regulatoryModel.regulatoryAffiliation;
    const affiliationDetails = this.regulatoryModel.affiliationDetails;
    const employeePubTrade = this.regulatoryModel.employeePubTrade;
    const controlPubTrade = this.regulatoryModel.controlPubTrade;

    this.regulatoryControlForm.patchValue({
      isOn: regulatoryControl?.status ?? null,
      details: regulatoryControl?.detail ?? '',
    }, {emitEvent: false});

    this.employeePubTradeForm.patchValue({
      isOn: employeePubTrade?.status ?? null,
      details: employeePubTrade?.detail ?? '',
    }, {emitEvent: false});

    this.controlPubTradeForm.patchValue({
      isOn: controlPubTrade?.status ?? null,
      details: controlPubTrade?.detail ?? '',
    }, {emitEvent: false});

    this.regulatoryAffiliationForm.patchValue({
      isOn: regulatoryAffiliation?.status ?? null,
      details: regulatoryAffiliation?.detail ?? '',
      relationship: affiliationDetails?.affiliationRelationship ?? '',
      personName: affiliationDetails?.personName ?? '',
      company: affiliationDetails?.company ?? '',
      country: affiliationDetails?.mailingCountry ?? '',
      state: affiliationDetails?.mailingState ?? '',
      city: affiliationDetails?.mailingCity ?? '',
      postalCode: affiliationDetails?.mailingPostalCode ?? '',
      address: affiliationDetails?.address1 ?? '',
      phone: affiliationDetails?.companyPhone ?? '',
      email: affiliationDetails?.companyEmail ?? '',
    }, {emitEvent: false});
    formLog.log('[Regulatory] model:', this.regulatoryModel);
    if (this.regulatoryModel.regulatoryControl?.status) {
      formLog.invalid('Regulatory.regulatoryControl', this.regulatoryControlForm);
    }
    if (this.regulatoryModel.employeePubTrade?.status) {
      formLog.invalid('Regulatory.employeePubTrade', this.employeePubTradeForm);
    }
    if (this.regulatoryModel.controlPubTrade?.status) {
      formLog.invalid('Regulatory.controlPubTrade', this.controlPubTradeForm);
    }
    if (this.regulatoryModel.regulatoryAffiliation?.status) {
      formLog.invalid('Regulatory.affiliation', this.regulatoryAffiliationForm);
    }
  }

  private syncModelFromForm(): void {
    if (!this.regulatoryModel) {
      return;
    }

    if (this.regulatoryModel.regulatoryControl) {
      const value = this.regulatoryControlForm.getRawValue();
      this.regulatoryModel.regulatoryControl.status = value.isOn;
      if (value.isOn) {
        this.regulatoryModel.regulatoryControl.detail = value.details;
      }
    }

    if (this.regulatoryModel.employeePubTrade) {
      const value = this.employeePubTradeForm.getRawValue();
      this.regulatoryModel.employeePubTrade.status = value.isOn;
      if (value.isOn) {
        this.regulatoryModel.employeePubTrade.detail = value.details;
      }
    }

    if (this.regulatoryModel.controlPubTrade) {
      const value = this.controlPubTradeForm.getRawValue();
      this.regulatoryModel.controlPubTrade.status = value.isOn;
      if (value.isOn) {
        this.regulatoryModel.controlPubTrade.detail = value.details;
      }
    }

    const affiliationValue = this.regulatoryAffiliationForm.getRawValue();
    if (this.regulatoryModel.regulatoryAffiliation) {
      this.regulatoryModel.regulatoryAffiliation.status = affiliationValue.isOn;
      if (affiliationValue.isOn) {
        this.regulatoryModel.regulatoryAffiliation.detail = affiliationValue.details;
      }
    }

    if (affiliationValue.isOn && this.regulatoryModel.affiliationDetails) {
      this.regulatoryModel.affiliationDetails.affiliationRelationship = affiliationValue.relationship;
      this.regulatoryModel.affiliationDetails.personName = affiliationValue.personName;
      this.regulatoryModel.affiliationDetails.company = affiliationValue.company;
      this.regulatoryModel.affiliationDetails.mailingCountry = affiliationValue.country;
      this.regulatoryModel.affiliationDetails.mailingState = affiliationValue.state;
      this.regulatoryModel.affiliationDetails.mailingCity = affiliationValue.city;
      this.regulatoryModel.affiliationDetails.mailingPostalCode = affiliationValue.postalCode;
      this.regulatoryModel.affiliationDetails.address1 = affiliationValue.address;
      this.regulatoryModel.affiliationDetails.companyPhone = affiliationValue.phone;
      this.regulatoryModel.affiliationDetails.companyEmail = affiliationValue.email;
    }
    formLog.log('[Regulatory] model:', this.regulatoryModel);
    if (this.regulatoryModel.regulatoryControl?.status) {
      formLog.invalid('Regulatory.regulatoryControl', this.regulatoryControlForm);
    }
    if (this.regulatoryModel.employeePubTrade?.status) {
      formLog.invalid('Regulatory.employeePubTrade', this.employeePubTradeForm);
    }
    if (this.regulatoryModel.controlPubTrade?.status) {
      formLog.invalid('Regulatory.controlPubTrade', this.controlPubTradeForm);
    }
    if (this.regulatoryModel.regulatoryAffiliation?.status) {
      formLog.invalid('Regulatory.affiliation', this.regulatoryAffiliationForm);
    }
  }

  private validateRegulatoryDetails() {
    if (this.featureService.newRegulatoryControl) {
      return (
        (!this.regulatoryModel.employeePubTrade.status ||
          this.employeePubTradeForm.valid) &&
        (!this.regulatoryModel.controlPubTrade.status ||
          this.controlPubTradeForm.valid)
      );
    }

    return (
      !this.regulatoryModel.regulatoryControl.status ||
      this.regulatoryControlForm.valid
    );
  }

  public switchRegulatoryControl() {
    this.regulatoryModel.regulatoryControl.status =
      !this.regulatoryModel.regulatoryControl.status;
    this.updateUser();
  }

  public switchAffiliationControl() {
    this.regulatoryModel.regulatoryAffiliation.status =
      !this.regulatoryModel.regulatoryAffiliation.status;
    this.updateUser();
  }

  public switchEmployeePubTrade() {
    this.regulatoryModel.employeePubTrade.status =
      !this.regulatoryModel.employeePubTrade.status;
    this.updateUser();
  }

  public switchControlPubTrade() {
    this.regulatoryModel.controlPubTrade.status =
      !this.regulatoryModel.controlPubTrade.status;
    this.updateUser();
  }

  private markAllFieldsAsTouched() {
    Object.keys(this.regulatoryControlForm.controls).forEach((key) => {
      this.regulatoryControlForm.get(key).markAsTouched();
      this.regulatoryControlForm.get(key).updateValueAndValidity();
    });

    Object.keys(this.regulatoryAffiliationForm.controls).forEach((key) => {
      this.regulatoryAffiliationForm.get(key).markAsTouched();
      this.regulatoryAffiliationForm.get(key).updateValueAndValidity();
    });

    Object.keys(this.employeePubTradeForm.controls).forEach((key) => {
      this.employeePubTradeForm.get(key).markAsTouched();
      this.employeePubTradeForm.get(key).updateValueAndValidity();
    });

    Object.keys(this.controlPubTradeForm.controls).forEach((key) => {
      this.controlPubTradeForm.get(key).markAsTouched();
      this.controlPubTradeForm.get(key).updateValueAndValidity();
    });
  }

  public updateUser(): void {
    this.service.updateUserWithoutValidation(this.userModel, () => {
    });
  }

  switchHasPoliticalFriends() {
    this.regulatoryModel.hasPoliticalFriends =
      !this.regulatoryModel.hasPoliticalFriends;
    this.updateUser();
  }

  filterCountryList(countryList: Array<Country>, filter: string) {
    if (filter && filter.trim().length > 0) {
      return countryList.filter((x) =>
        x.countryTranslation.toLowerCase().startsWith(filter.toLowerCase())
      );
    }

    return countryList;
  }

  getCountryCode(citizenship: string): string {
    if (
      citizenship &&
      this.countryList &&
      this.countryList.some((x) => x.countryName === citizenship)
    ) {
      return this.countryList.find((x) => x.countryName === citizenship)
        .countryCode;
    }

    return '';
  }

  getTranslate(country: string) {
    if (
      !country ||
      !this.countryList ||
      !this.countryList.some((x) => x.countryName === country)
    ) {
      return '';
    }

    return this.countryList.find((x) => x.countryName === country)
      .countryTranslation;
  }

  getStatesByCountry(countryName: string): Array<StateInfo> {
    if (countryName && this.countryList) {
      const country = this.countryList.find(
        (x) => x.countryName === countryName
      );
      return country.states;
    }

    return new Array<StateInfo>();
  }

  getStateName(countryName: string, stateCode: string) {
    if (countryName && stateCode) {
      const countryStates = this.getStatesByCountry(countryName);
      if (countryStates.some((x) => x.code === stateCode)) {
        return countryStates.find((x) => x.code === stateCode).name;
      }
    }

    return '';
  }

  onCountryChanged(reset: boolean = true) {
    if (reset) {
      this.regulatoryModel.affiliationDetails.mailingState = null;
      this.updateUser();
    }

    const hasStates =
      this.getStatesByCountry(
        this.regulatoryModel.affiliationDetails.mailingCountry
      ).length > 0;
    const state = this.regulatoryAffiliationForm.controls.state;
    if (hasStates) {
      state.enable();
    } else {
      state.disable();
    }
    state.setValidators(hasStates ? Validators.required : null);
    if (reset) {
      state.setValue(null);
    }
  }
}
